import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  signIn,
  signOut,
  confirmSignIn,
  signInWithRedirect,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import AuthLayout from "../../layouts/AuthLayout";
import { useRedirectIfAuthenticated } from "../../hooks/useRedirectIfAuthenticated";
import { DeviceAPI } from "../../services/device.service";
import { getDeviceId } from "../../utils/device-id";
import { ApiError, NotAuthenticatedError } from "../../services/api.errors";
import PasswordCriteria from "../../components/auth/PasswordCriteria";
import { validatePasswordStrength } from "../../utils/password-policy";
import { reportCognitoFailure } from "../../utils/flow-telemetry";
import {
  isTransientAuthSession,
  withTransientAuthSession,
} from "../../utils/transient-auth-session";

type Step = "login" | "newPassword" | "deviceVerify";



const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Device verification step state
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { redirectAfterLogin } = useRedirectIfAuthenticated({
    onError: setError,
    onDeviceVerificationRequired: () => {
      setStep("deviceVerify");
      setError(null);
    },
  });

  // SignUpPage routes here after confirming a restored session, passing its
  // success copy through router state. Consume it once, then strip it so a
  // refresh does not resurrect a stale banner.
  useEffect(() => {
    const incoming = (location.state as { info?: string } | null)?.info;
    if (incoming) {
      setInfo(incoming);
      navigate("/login", { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Listen for Amplify auth events — handle both successful sign-in and
    // OAuth redirect failures (e.g. user cancelled, token exchange error).
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn" && !cancelled) {
        // Ignore the transient sign-in that confirmSignIn triggers while the
        // doctor is setting their first password: handleSetNewPassword owns
        // that flow and routes them back to login itself. The flag is shared
        // with the other `signedIn` listeners (security-lifecycle,
        // AuthInterceptor), which must stand down for the same reason.
        if (isTransientAuthSession()) return;
        redirectAfterLogin();
      }
      if (payload.event === "signInWithRedirect_failure" && !cancelled) {
        console.error("OAuth redirect failure:", payload.data);
        setError("Google sign-in failed. Please try again.");
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [redirectAfterLogin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setIsSubmitting(true);

    // Set once Cognito has answered, so a failure in the redirect that follows
    // is not reported as a sign-in failure.
    let cognitoAnswered = false;
    try {
      let result;
      try {
        result = await signIn({ username: email.trim(), password });
      } catch (signInErr: any) {
        // Stale session — clear and retry once
        if (signInErr?.name === "UserAlreadyAuthenticatedException") {
          await signOut().catch(() => {});
          result = await signIn({ username: email.trim(), password });
        } else {
          throw signInErr;
        }
      }

      cognitoAnswered = true;
      const { isSignedIn, nextStep } = result;

      // Admin-provisioned doctors sign in with a Cognito temporary password and
      // must choose their own password before the account is usable.
      if (nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setStep("newPassword");
        return;
      }

      if (!isSignedIn) {
        setError("Additional verification is required for this account.");
        return;
      }

      await redirectAfterLogin();
    } catch (err: any) {
      if (err?.name === "UserAlreadyAuthenticatedException") {
        // Session still active — redirect instead of showing an error.
        await redirectAfterLogin();
        return;
      }
      // The account exists but never completed email verification. Cognito
      // refuses both sign-in and password reset in this state, so the only way
      // out is a fresh confirmation code. Hand off to the signup verify step,
      // which sends one on arrival.
      if (err?.name === "UserNotConfirmedException") {
        navigate("/signup", { state: { verifyEmail: email.trim() } });
        return;
      }
      if (!cognitoAnswered) reportCognitoFailure("COGNITO_SIGNIN_FAILED", err);
      const message =
        err?.name === "NotAuthorizedException"
          ? "Incorrect email or password."
          : err?.message || "Unable to sign in. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    const strengthError = validatePasswordStrength(newPassword);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      await withTransientAuthSession(async () => {
        await confirmSignIn({ challengeResponse: newPassword });

        // Password set. Per the agreed flow, return the doctor to the login
        // screen to sign in normally with their new password rather than
        // dropping them straight into the app.
        await signOut().catch(() => {});
        setStep("login");
        setPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setInfo("Password set. Please sign in with your new password.");
      });
    } catch (err: any) {
      reportCognitoFailure("COGNITO_SIGNIN_FAILED", err);
      const message =
        err?.message || "We could not set your password. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      // Clear any stale session before starting OAuth — Amplify throws
      // UserAlreadyAuthenticatedException if a previous session lingers.
      try { await signOut(); } catch { /* ignore if not signed in */ }
      await signInWithRedirect({ provider: "Google" });
    } catch (err: any) {
      console.error("Google sign-in error:", err);
      setError(err?.message || "Could not start Google sign in. Please try again.");
    }
  };

  // The session died under the OTP step: the setup flow signed out of it, or a
  // refresh outlived it. Nothing here can succeed without a token, so drop back
  // to the login form with the reason shown rather than printing it under the
  // code boxes next to a "Continue" button that cannot work.
  const fallBackToLogin = (err: NotAuthenticatedError) => {
    setStep("login");
    setOtp(Array(6).fill(""));
    setOtpSent(false);
    setError(null);
    setInfo(err.message);
  };

  const goToForgotPassword = () => navigate("/forgot-password");
  const goToSignUp = () => navigate("/signup");

  // Send OTP whenever we enter the device verify step
  useEffect(() => {
    if (step !== "deviceVerify") return;
    let cancelled = false;
    const send = async () => {
      setOtpSending(true);
      setError(null);
      try {
        const res = await DeviceAPI.check(getDeviceId());
        if (!cancelled) {
          if (res.trusted) {
            // Already trusted (race) — proceed straight into the app
            await redirectAfterLogin();
          } else {
            setOtpSent(true);
            setResendCooldown(60);
          }
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotAuthenticatedError) {
          fallBackToLogin(err);
          return;
        }
        setError(err instanceof ApiError ? err.message : "Could not send verification code.");
      } finally {
        if (!cancelled) setOtpSending(false);
      }
    };
    send();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleOtpChange = (idx: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[idx] = value;
    setOtp(next);
    if (value && idx < 5) inputRefs.current[idx + 1]?.focus();
    if (next.every(d => d) && next.join("").length === 6) handleOtpVerify(next.join(""));
  };

  const handleOtpKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) inputRefs.current[idx - 1]?.focus();
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = Array(6).fill("");
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setOtp(next);
    inputRefs.current[Math.min(text.length, 5)]?.focus();
    if (text.length === 6) handleOtpVerify(text);
  };

  const handleOtpVerify = async (code: string) => {
    if (otpVerifying) return;
    setOtpVerifying(true);
    setError(null);
    try {
      await DeviceAPI.verify(getDeviceId(), code);
      // Keep otpVerifying = true so the button shows "Signing in…" and stays
      // locked while redirectAfterLogin navigates away. Never reset on success.
      await redirectAfterLogin();
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        setOtpVerifying(false);
        fallBackToLogin(err);
        return;
      }
      setError(err instanceof ApiError ? err.message : "Verification failed.");
      setOtp(Array(6).fill(""));
      setOtpVerifying(false);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
  };

  const handleResendOtp = async () => {
    setOtpSending(true);
    setError(null);
    try {
      const res = await DeviceAPI.check(getDeviceId(), undefined, true);
      if (!res.otp_sent) {
        setError("Could not send a new code. Please try again.");
        return;
      }
      setResendCooldown(60);
      setOtp(Array(6).fill(""));
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        fallBackToLogin(err);
        return;
      }
      setError(err instanceof ApiError ? err.message : "Could not resend code.");
    } finally {
      setOtpSending(false);
    }
  };

  if (step === "deviceVerify") {
    return (
      <AuthLayout
        title="Verify your device"
        subtitle={otpSending ? "Sending verification code…" : otpSent ? "Enter the 6-digit code we sent to your email." : "We'll send a 6-digit code to your email."}
        heroTitleLine1="One more step."
        heroTitleLine2="Confirm it's you"
      >
        <div className="space-y-5">
          <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
            {otp.map((digit, idx) => (
              <input
                key={idx}
                ref={el => { inputRefs.current[idx] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleOtpChange(idx, e.target.value)}
                onKeyDown={e => handleOtpKeyDown(idx, e)}
                disabled={otpVerifying || !otpSent}
                className={`w-11 h-12 rounded-xl border text-center text-lg font-semibold text-slate-900
                  outline-none transition-all
                  ${digit ? "border-[#0066ff] bg-blue-50" : "border-slate-200 bg-slate-50"}
                  focus:border-[#0066ff] focus:ring-2 focus:ring-blue-100
                  disabled:opacity-40`}
              />
            ))}
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </p>
          )}

          <button
            onClick={() => handleOtpVerify(otp.join(""))}
            disabled={otp.join("").length < 6 || otpVerifying || !otpSent}
            className="w-full inline-flex items-center justify-center min-h-[44px] rounded-full bg-[#0066ff] px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#0051cc] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {otpVerifying ? "Signing in…" : "Continue"}
          </button>

          <div className="flex flex-col items-center gap-3 pt-1">
            <button
              onClick={handleResendOtp}
              disabled={resendCooldown > 0 || otpSending || otpVerifying}
              className="inline-flex items-center justify-center min-h-[44px] rounded-full border border-[#0066ff] px-6 py-2.5 text-sm font-medium text-[#0066ff] hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : otpSending ? "Sending…" : "Resend code"}
            </button>
            <button
              onClick={async () => {
                try { await signOut(); } catch { /* best-effort */ }
                navigate("/login", { replace: true });
                setStep("login");
                setOtp(Array(6).fill(""));
                setOtpSent(false);
              }}
              disabled={otpVerifying}
              className="text-sm font-medium text-slate-500 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (step === "newPassword") {
    return (
      <AuthLayout
        title="Set your password"
        subtitle="Choose a password to finish setting up your clinic account."
        heroTitleLine1="Almost there!"
        heroTitleLine2="Set your password to continue"
      >
        <form onSubmit={handleSetNewPassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600">
              New password
            </label>
            <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <input
                type={showNewPassword ? "text" : "password"}
                required
                autoComplete="new-password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                placeholder="Enter a new password"
              />
              <button type="button" onClick={() => setShowNewPassword(v => !v)} className="ml-2 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600" aria-label={showNewPassword ? "Hide password" : "Show password"}>
                {showNewPassword ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
              </button>
            </div>
            <PasswordCriteria password={newPassword} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600">
              Confirm password
            </label>
            <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <input
                type={showConfirmPassword ? "text" : "password"}
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                placeholder="Re-enter new password"
              />
              <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="ml-2 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600" aria-label={showConfirmPassword ? "Hide password" : "Show password"}>
                {showConfirmPassword ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-1 inline-flex items-center justify-center min-h-[44px] rounded-full bg-[#0066ff] px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#0051cc] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Saving..." : "Set password"}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Login"
      subtitle="Use your clinic email and password to continue."
      heroTitleLine1="Welcome back,"
      heroTitleLine2="Login to continue"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-600">
            Email
          </label>
          <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <input
              id="email"
              type="email"
              required
              name="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
              placeholder="Enter your email"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-600">
            Password
          </label>
          <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              required
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
              placeholder="Enter your password"
            />
            <button type="button" onClick={() => setShowPassword(v => !v)} className="ml-2 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600" aria-label={showPassword ? "Hide password" : "Show password"}>
              {showPassword ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
            </button>
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            {error}
          </p>
        )}
        {info && (
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3">
            {info}
          </p>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={goToForgotPassword}
            className="text-sm text-[#0066cc] hover:underline"
          >
            Forgot Password?
          </button>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-1 w-32 inline-flex items-center justify-center min-h-[44px] rounded-full bg-[#0066ff] px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#0051cc] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Logging in..." : "Login"}
        </button>

        <div className="pt-4 space-y-2">
          <p className="text-sm text-slate-500">Or login with</p>
          <div className="flex items-center gap-3">

            <button
              type="button"
              disabled
              title="Apple sign-in coming soon"
              className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm opacity-40 cursor-not-allowed"
              aria-label="Sign in with Apple (not available)"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6 fill-slate-900">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm hover:bg-slate-50 transition-colors"
              aria-label="Sign in with Google"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="pt-4 text-sm text-slate-500">
          Do not have an account?
          <button
            type="button"
            onClick={goToSignUp}
            className="ml-1 font-medium text-[#0066cc] hover:underline text-sm"
          >
            Sign up
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
